import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";

const FooterCol = ({ title, children }) => (
  <div>
    <h4 className="overline mb-4 text-white/70">{title}</h4>
    <ul className="space-y-3">{children}</ul>
  </div>
);

const FooterLink = ({ to, label, testid, external }) => (
  <li>
    {external ? (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-white/80 hover:text-white transition-colors"
        data-testid={testid}
      >
        {label}
      </a>
    ) : (
      <Link
        to={to}
        className="text-sm text-white/80 hover:text-white transition-colors"
        data-testid={testid}
      >
        {label}
      </Link>
    )}
  </li>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      if (res.already_subscribed) {
        toast("You're already on the list.");
      } else {
        toast.success("You're on the list. Drops, colourways and boot pairings inbound.");
      }
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink text-white" data-testid="site-footer">
      <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="wordmark text-4xl md:text-5xl uppercase font-bold leading-[1]">
            Pritch
          </div>
          <div className="subwordmark text-xs md:text-sm text-white/60 mt-3">Premium Pitch Wear</div>
          <p className="mt-6 max-w-md text-white/70 text-sm leading-relaxed">
            Football essentials for players who care about the details. Starting from the ground up
            with premium grip socks.
          </p>

          <form
            onSubmit={onSubmit}
            className="mt-8 flex border border-white/20"
            data-testid="footer-newsletter-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 bg-transparent px-4 py-4 text-sm placeholder:text-white/40 focus:outline-none"
              data-testid="footer-newsletter-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-white text-ink px-6 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-pritch-volt-trim transition-colors disabled:opacity-50"
              data-testid="footer-newsletter-submit"
            >
              {loading ? "Joining…" : "Join the list"}
            </button>
          </form>
        </div>

        <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
          <FooterCol title="Shop">
            <FooterLink to="/shop" label="Grip Socks" testid="footer-link-grip-socks" />
            <FooterLink to="/grips" label="Grip System" testid="footer-link-grip-system" />
            <FooterLink to="/bundles" label="Bundles" testid="footer-link-bundles" />
            <FooterLink to="/team-orders" label="Team Orders" testid="footer-link-team-orders" />
          </FooterCol>
          <FooterCol title="Help">
            <FooterLink to="/product/pritch-grip-sock#size-guide" label="Size Guide" testid="footer-link-size-guide" />
            <FooterLink to="/contact" label="Delivery" testid="footer-link-delivery" />
            <FooterLink to="/contact" label="Returns" testid="footer-link-returns" />
            <FooterLink to="/contact" label="Contact" testid="footer-link-contact" />
          </FooterCol>
          <FooterCol title="Brand">
            <FooterLink to="/about" label="About" testid="footer-link-about" />
            <FooterLink to="/team-orders" label="Clubs & Academies" testid="footer-link-clubs" />
          </FooterCol>
          <FooterCol title="Social">
            <FooterLink to="https://instagram.com" external label="Instagram" testid="footer-link-instagram" />
            <FooterLink to="https://tiktok.com" external label="TikTok" testid="footer-link-tiktok" />
            <FooterLink to="mailto:hello@pritch.fc" external label="hello@pritch.fc" testid="footer-link-email" />
          </FooterCol>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Pritch — Premium Pitch Wear</span>
          <span className="mono">Grip. Fit. Finish.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
